import React, { useState, useEffect, useCallback, useMemo } from 'react';
import '@fortawesome/fontawesome-free/css/all.min.css';
import {
  Button,
  Modal,
  Badge,
  Card,
  Form,
  Spinner,
  Alert,
} from 'react-bootstrap';
import './CashierPortal.css';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

import { FaBoxOpen, FaSignOutAlt, FaSearch } from 'react-icons/fa';
import BASE_URL, { BACKEND_URL } from '../config';

// ✅ Axios instance with Bearer token
const authAxios = axios.create({
  baseURL: BASE_URL,
});

authAxios.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Normalizes common Laravel pagination/serializer response shapes.
// Supports: {data:{data:[...]}} and {data:[...]} and direct arrays.
const extractListArray = (res) => {
  const data = res?.data;
  return data?.data?.data || data?.data || data || [];
};

// Function to format payment method for display
const formatPaymentMethod = (method) => {
  if (!method) return 'N/A';
  // Replace underscores with spaces and capitalize each word
  return method
    .replace(/_/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Function to map raw API attachments to structured format (from AdminDashboard)
const mapCollectionAttachments = (collection) => {
  // Debug: Log the collection to see its structure
  console.log('Mapping collection attachments:', collection);

  // The API provides a flat array of attachments.
  const rawAttachments = Array.isArray(collection.attachments)
    ? collection.attachments
    : [];

  // Categorize the attachments based on their 'type' property.
  const pod_photos = rawAttachments.filter(
    (a) =>
      a.type?.toLowerCase() === 'pod_photo' ||
      a.type?.toLowerCase() === 'cheque_photo'
  );
  const voice_notes = rawAttachments.filter(
    (a) => a.type?.toLowerCase() === 'voice_note'
  );
  const order_docs = rawAttachments.filter(
    (a) =>
      a.type?.toLowerCase() === 'order_document' ||
      a.type?.toLowerCase() === 'other'
  );

  const addUrl = (file) => ({
    ...file,
    url: file.path ? `${BACKEND_URL}/${file.path}` : null,
  });

  return {
    ...collection,
    // This is the structure expected by the CollectionDetailsModal
    attachments: {
      pod_photos: pod_photos.map(addUrl), // e.g., PODs, Cheque Photos
      voice_notes: voice_notes.map(addUrl), // Voice notes
      order_docs: order_docs.map(addUrl), // Other documents
    },
  };
};

// --- NEW COMPONENT: Collection Details Modal ---
const CollectionDetailsModal = ({
  show,
  handleClose,
  collection,
  getCustomerName,
  getVendorName,
}) => {
  // HOOKS HOISTED TO TOP LEVEL OF THIS COMPONENT
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const [attachmentError, setAttachmentError] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [detailedCollection, setDetailedCollection] = useState(null); // State for full collection data

  // ⭐ FIX: Fetch attachments using the same approach as AdminDashboard ⭐
  const fetchAttachments = async (id) => {
    setLoadingAttachments(true);
    setAttachmentError(null);
    setDetailedCollection(null); // Reset detailed view
    try {
      console.log('Fetching attachments for collection ID:', id);

      // ALWAYS fetch the full collection details to get complete attachments and notes.
      // The list view might provide summarized data.
      let endpoint = `/collections/${id}`;
      if (collection?.type === 'Payment') {
        endpoint = `/payments/${id}`;
      }

      const res = await authAxios.get(endpoint);
      console.log('Collection details response:', res.data);

      const collectionData = res.data?.data || res.data;
      const mappedCollection = mapCollectionAttachments(collectionData);

      const allAttachments = [
        ...(mappedCollection.attachments.pod_photos || []),
        ...(mappedCollection.attachments.voice_notes || []),
        ...(mappedCollection.attachments.order_docs || []),
      ];

      console.log('All attachments after mapping:', allAttachments);
      setAttachments(allAttachments);
      setDetailedCollection(collectionData); // Store the full data
    } catch (error) {
      console.error('Failed to fetch collection attachments:', error);
      setAttachmentError('Failed to load attachments');
    } finally {
      setLoadingAttachments(false);
    }
  };

  // useEffect to fetch attachments when modal opens
  useEffect(() => {
    if (show && collection) {
      let realId = collection.id;
      // Handle prefixed IDs from history (e.g., "payment-123")
      if (collection.original_id) {
        realId = collection.original_id;
      }
      fetchAttachments(realId);
    } else {
      setAttachments([]);
      setAttachmentError(null);
    }
  }, [show, collection?.id]);

  if (!collection) return null;

  // ⭐ FIX: Updated handleOpenAttachment to match AdminDashboard approach ⭐
  const handleOpenAttachment = (file) => {
    if (!file.path) {
      setAttachmentError('Attachment path is missing');
      return;
    }
    const fileUrl = `${BACKEND_URL}/${file.path}`;
    console.log('Opening attachment URL:', fileUrl);
    window.open(fileUrl, '_blank');
  };

  const entityName = collection.customer_id
    ? `Customer: ${getCustomerName(collection.customer_id)}`
    : collection.vendor_id
      ? `Vendor: ${getVendorName(collection.vendor_id)}`
      : 'Entity: Unknown';

  // Use the detailed collection if available, otherwise fallback to the initial one
  const displayCollection = detailedCollection || collection;

  return (
    <Modal show={show} onHide={handleClose} size='lg' centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {displayCollection.type || 'Collection'} Request Details - #
          {displayCollection.uuid || displayCollection.id}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Card className='p-3 mb-3'>
          <p>
            <strong>{entityName}</strong>
          </p>
          <p>
            <strong>Amount:</strong> AED {displayCollection.amount || '0'}
          </p>
          <p>
            <strong>Status:</strong>{' '}
            <Badge bg='info'>{displayCollection.status || 'N/A'}</Badge>
          </p>
          <p>
            <strong>Method:</strong> {formatPaymentMethod(displayCollection.method)}
          </p>
          <p>
            <strong>Driver:</strong>{' '}
            {displayCollection.driver_name ||
              displayCollection.assigned_to?.name ||
              'Unassigned'}
          </p>
          <p>
            <strong>Notes:</strong>{' '}
            {displayCollection.notes || 'No notes available.'}
          </p>
          <p>
            <strong>Created At:</strong>{' '}
            {displayCollection.created_at
              ? new Date(displayCollection.created_at).toLocaleString()
              : 'N/A'}
          </p>
        </Card>

        <Card className='p-3'>
          <h5>Attachments</h5>
          {loadingAttachments ? (
            <div className='d-flex align-items-center'>
              <Spinner animation='border' size='sm' className='me-2' />
              <p>Loading files...</p>
            </div>
          ) : attachmentError ? (
            <Alert variant='danger'>{attachmentError}</Alert>
          ) : attachments.length > 0 ? (
            <div className='d-flex flex-wrap gap-2 mt-2'>
              {attachments.map((file, idx) => (
                <Button
                  key={idx}
                  variant='outline-primary'
                  size='sm'
                  onClick={() => handleOpenAttachment(file)}
                  className='d-flex align-items-center gap-1'
                  disabled={!file.url}
                >
                  <FaBoxOpen />
                  {file.type?.replace(/_/g, ' ') || `File ${idx + 1}`}
                </Button>
              ))}
            </div>
          ) : (
            <p className='text-muted'>No attachments found.</p>
          )}
        </Card>
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={handleClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// --- END: Collection Details Modal ---

const CashierPortal = () => {
  // ⭐ HOOKS HOISTED TO TOP LEVEL OF THIS COMPONENT ⭐
  // Simplified multiple modal states into one object for cleaner code,
  // but kept original logic for assignment simplicity.
  const [assignModal, setAssignModal] = useState(false);
  const [viewedRequest, setViewedRequest] = useState(null);
  const [summary, setSummary] = useState({});
  const [activePendingCollections, setActivePendingCollections] = useState([]);
  const [activeCollections, setActiveCollections] = useState([]);
  // ✅ Single source of truth for Collected tab
  const [collectedCollections, setCollectedCollections] = useState([]);
  const [loadingCollected, setLoadingCollected] = useState(false);
  const [errorCollected, setErrorCollected] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [vendors, setVendors] = useState([]);

  // 🔹 Assign driver
  const [selectedDriver, setSelectedDriver] = useState('');
  const [assignedDriver, setAssignedDriver] = useState('');
  const [selectedCollectionId, setSelectedCollectionId] = useState(null);

  // 🔹 Filter for history
  // Helper function to get first day of current month in YYYY-MM-DD format
  const getFirstDayOfMonth = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}-01`;
  };

  // Helper function to get last day of current month in YYYY-MM-DD format
  const getLastDayOfMonth = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const lastDay = new Date(year, now.getMonth() + 1, 0).getDate();
    return `${year}-${month}-${String(lastDay).padStart(2, '0')}`;
  };

  const [historyFilter, setHistoryFilter] = useState({
    from: getFirstDayOfMonth(),
    to: getLastDayOfMonth(),
  });

  // 🔹 User information state
  const [userInfo, setUserInfo] = useState({ name: '', branch: '' });

  // States for the approval modal, based on PaymentPortal.js
  const [show, setShow] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [modalType, setModalType] = useState(null);

  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [activeForm, setActiveForm] = useState('formC');
  const [expandedCollections, setExpandedCollections] = useState(new Set());

  // 🔹 Navigation for logout
  const navigate = useNavigate();

  // --- Modal Open/Close Handlers ---
  const handleOpenDetailsModal = (request) => {
    setViewedRequest(request);
    setShowDetailsModal(true);
  };

  const handleCloseDetailsModal = () => {
    setViewedRequest(null);
    setShowDetailsModal(false);
  };

  // --- Logout Handler - Same as SalesForm ---
  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userId');
    navigate('/');
  };
  // --- End Modal Handlers ---

  const fetchCollected = useCallback(async () => {
    setLoadingCollected(true);
    setErrorCollected(null);

    try {
      // Cashier Portal contract: list is driven ONLY by status=collected
      const res = await authAxios.get('/collections', {
        params: { status: 'collected' },
      });

      const list = extractListArray(res);
      if (!Array.isArray(list)) {
        throw new Error('Unexpected API response shape');
      }

      // Deduplicate by collection ID before setting state
      const seen = new Map();
      list.forEach(item => {
        const key = String(item.id || item.uuid); // Convert to string for consistent comparison
        if (key && key !== 'undefined' && !seen.has(key)) {
          seen.set(key, item);
        }
      });
      const deduplicatedList = Array.from(seen.values());

      console.log('Initial list count:', list.length);
      console.log('Deduplicated list count:', deduplicatedList.length);

      setCollectedCollections(deduplicatedList);

      // Enrich with payment details in background to check cashier_approved status
      (async () => {
        for (const item of deduplicatedList) {
          try {
            const detailRes = await authAxios.get(`/collections/${item.id || item.uuid}`);
            const detailData = detailRes.data?.data || detailRes.data;
            if (detailData) {
              console.log(`Collection ${item.id} payments:`, detailData.payments);
              setCollectedCollections(prev => {
                // Ensure we maintain deduplication during enrichment
                const updated = prev.map(req => {
                  const reqId = String(req.id || req.uuid);
                  const itemId = String(item.id || item.uuid);
                  return reqId === itemId ? { ...req, ...detailData } : req;
                });
                return updated;
              });
            }
          } catch (err) {
            console.error('Failed to enrich collection details:', err);
          }
        }
      })();
    } catch (err) {
      console.error('Failed to fetch collected collections', err);
      setErrorCollected('Failed to load collected collections');
      setCollectedCollections([]);
    } finally {
      setLoadingCollected(false);
    }
  }, []);

  // Modal handlers from PaymentPortal
  const handleOpenModal = async (request, type) => {
    setSelectedRequest(request);
    setModalType(type);

    // If it's an approve action, fetch the full collection details
    if (type === 'approve' || type === 'assign') {
      try {
        const collectionId = request.id || request.uuid;
        const collectionRes = await authAxios.get(
          `/collections/${collectionId}`
        );

        const collectionData = collectionRes.data?.data || collectionRes.data;

        // Update selectedRequest with full collection data
        setSelectedRequest({
          ...request,
          ...collectionData,
        });
      } catch (err) {
        console.error('Error fetching collection details:', err);
        // Continue with the original request data if fetch fails
      }
    }

    setShow(true);
  };

  const handleClose = () => {
    setSelectedRequest(null);
    setModalType(null);
    setShow(false);
  };

  const handleDeleteItem = async (itemId, itemType) => {
    if (!window.confirm('Are you sure you want to delete this record? This action cannot be undone.')) return;

    try {
      const [type, realId] = itemId.toString().split('-');
      const endpoint = type === 'payment' ? `/payments/${realId}` : `/collections/${realId}`;
      await authAxios.delete(endpoint);
      setHistoryData(prev => prev.filter(item => item.id !== itemId));
      alert('Deleted successfully.');
    } catch (err) {
      console.error('Error deleting record:', err.response || err);
      alert(err.response?.data?.message || 'Failed to delete record.');
    }
  };

  // ⭐ useEffect HOISTED TO TOP LEVEL OF THIS COMPONENT ⭐
  // ✅ Get user information from localStorage
  useEffect(() => {
    // Get user information from localStorage
    const userName = localStorage.getItem('userName') || 'Unknown User';
    const branchName = localStorage.getItem('branchName') || 'Unknown Branch';

    setUserInfo({
      name: userName,
      branch: branchName,
    });
  }, []);

  // ⭐ useEffect HOISTED TO TOP LEVEL OF THIS COMPONENT ⭐
  // ✅ Fetch dashboard summary
  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const res = await authAxios.get('/dashboard/cashier-summary');
        // Extract data from response: { success: true, data: { requests_due, amount_due, active_collections } }
        const summaryData = res.data?.data || res.data || {};
        setSummary(summaryData);
      } catch (err) {
        console.error('Failed to fetch summary:', err);
        setSummary({});
      }
    };
    fetchSummary();
  }, []);

  // ⭐ useEffect HOISTED TO TOP LEVEL OF THIS COMPONENT ⭐
  // ✅ Fetch customers
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const res = await authAxios.get('/customers');
        const customersData = res.data?.data?.data || [];
        setCustomers(Array.isArray(customersData) ? customersData : []);
      } catch (err) {
        console.error('Failed to fetch customers:', err);
        setCustomers([]);
      }
    };
    fetchCustomers();
  }, []);

  // ⭐ useEffect HOISTED TO TOP LEVEL OF THIS COMPONENT ⭐
  // ✅ Fetch vendors
  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const res = await authAxios.get('/vendors');
        const vendorsData = res.data?.data?.data || [];
        setVendors(Array.isArray(vendorsData) ? vendorsData : []);
      } catch (err) {
        console.error('Failed to fetch vendors:', err);
        setVendors([]);
      }
    };
    fetchVendors();
  }, []);

  // ⭐ useEffect HOISTED TO TOP LEVEL OF THIS COMPONENT ⭐
  // ✅ Fetch active pending collections (Pending tab)
  useEffect(() => {
    const fetchActivePendingCollections = async () => {
      try {
        let allPendingCollections = [];
        let page = 1;
        let hasMore = true;

        // Fetch all pages if paginated
        while (hasMore) {
          const res = await authAxios.get('/collections', {
            params: {
              status: 'created',
              page: page,
              per_page: 100, // Fetch 100 items per page
              start_date: getFirstDayOfMonth(),
              end_date: getLastDayOfMonth(),
            },
          });

          console.log(`Page ${page} raw response:`, res.data);

          // Try multiple response structures
          let collectionsData = [];

          // Check various response structures
          if (res.data?.data?.data && Array.isArray(res.data.data.data)) {
            // Nested paginated: { data: { data: [...], last_page: X } }
            collectionsData = res.data.data.data;
            hasMore = page < (res.data.data.last_page || 1);
          } else if (res.data?.data && Array.isArray(res.data.data)) {
            // Paginated: { data: [...], last_page: X }
            collectionsData = res.data.data;
            hasMore = page < (res.data.last_page || 1);
          } else if (Array.isArray(res.data?.data)) {
            collectionsData = res.data.data;
            hasMore = false;
          } else if (Array.isArray(res.data)) {
            collectionsData = res.data;
            hasMore = false;
          } else {
            console.warn(
              `Page ${page}: Unexpected response structure:`,
              res.data
            );
            hasMore = false;
          }

          // Filter out null/undefined items and ensure all required fields exist
          const validCollections = collectionsData
            .filter((item) => {
              // Filter out null, undefined, or empty objects
              if (!item || typeof item !== 'object') {
                return false;
              }
              // Ensure item has at least an id or uuid
              return !!(item.id || item.uuid);
            })
            .map((item) => {
              // Normalize the item with safe defaults
              const normalized = {
                ...item,
                id: item.id ?? item.uuid ?? null,
                customer_id: item.customer_id ?? item.customerId ?? null,
                vendor_id: item.vendor_id ?? item.vendorId ?? null,
                amount: item.amount ?? 0,
                method: item.method ?? 'N/A',
                status: item.status ?? 'created',
                uuid: item.uuid ?? item.id ?? null,
              };

              // Log if we find null values in critical fields
              if (normalized.id === null) {
                console.warn('Collection item missing id/uuid:', item);
              }

              return normalized;
            });

          allPendingCollections = [
            ...allPendingCollections,
            ...validCollections,
          ];
          console.log(
            `Fetched page ${page}: ${collectionsData.length} raw items, ${validCollections.length} valid items, Total: ${allPendingCollections.length}`
          );

          if (hasMore && validCollections.length > 0) {
            page++;
          } else {
            break;
          }
        }

        console.log('Final pending collections data:', allPendingCollections);
        console.log('Sample item:', allPendingCollections[0]);
        setActivePendingCollections(allPendingCollections);
      } catch (err) {
        console.error('Failed to fetch pending collections:', err);
        console.error('Error details:', err.response?.data || err.message);
        setActivePendingCollections([]);
      }
    };
    fetchActivePendingCollections();
  }, []);

  // ⭐ useEffect HOISTED TO TOP LEVEL OF THIS COMPONENT ⭐
  // ✅ Fetch active collections (Assigned )
  useEffect(() => {
    const fetchActiveCollections = async () => {
      try {
        const res = await authAxios.get('/collections', {
          params: {
            status: 'assigned',
            start_date: getFirstDayOfMonth(),
            end_date: getLastDayOfMonth(),
          },
        });
        const collectionsData = res.data?.data || [];

        // Don't map attachments here, let the modal handle it
        setActiveCollections(
          Array.isArray(collectionsData) ? collectionsData : []
        );
      } catch (err) {
        console.error('Failed to fetch active collections:', err);
        setActiveCollections([]);
      }
    };
    fetchActiveCollections();
  }, []);

  // ✅ Correct API usage (matches curl exactly)
  useEffect(() => {
    fetchCollected();
  }, [fetchCollected]);

  // ⭐ useEffect HOISTED TO TOP LEVEL OF THIS COMPONENT ⭐
  // ✅ Fetch history (now a useCallback)
  const fetchHistory = useCallback(
    async (filters = { driver: 'All', from: null, to: null }) => {
      try {
        const params = {};

        // Add date filters (backend uses different names for collections vs payments)
        if (filters.from) {
          params.start_date = filters.from; // for collections
          params.from_date = filters.from;  // for payments
        }
        if (filters.to) {
          params.end_date = filters.to;     // for collections
          params.to_date = filters.to;       // for payments
        }

        // Add driver filter if not 'All'
        if (filters.driver && filters.driver !== 'All') {
          // Find driver ID by name
          const driver = drivers.find((d) => d.name === filters.driver);
          if (driver) {
            params.driver_id = driver.id;
          }
        }

        const [collectionsRes, paymentsRes] = await Promise.all([
          authAxios.get('/collections', { params }),
          authAxios.get('/payments', { params }),
        ]);

        let collectionsData = [];
        const responseData = collectionsRes.data?.data || collectionsRes.data;

        // Handle paginated response
        if (responseData?.data && Array.isArray(responseData.data)) {
          collectionsData = responseData.data;
        } else if (Array.isArray(responseData)) {
          collectionsData = responseData;
        } else if (Array.isArray(collectionsRes.data)) {
          collectionsData = collectionsRes.data;
        }

        // Filter out null/undefined items and normalize
        const validCollections = collectionsData
          .filter((item) => item !== null && item !== undefined)
          .map((item) => ({
            ...item,
            id: `collection-${item.id || item.uuid}`,
            original_id: item.id || item.uuid,
            customer_id: item.customer_id || null,
            vendor_id: item.vendor_id || null,
            amount: item.amount || 0,
            method: item.method || 'N/A',
            status: item.status || 'created',
            type: 'Collection',
            created_at: item.created_at,
          }));

        // Process Payments
        let paymentsData = [];
        const paymentsResponseData = paymentsRes.data?.data || paymentsRes.data;
        if (paymentsResponseData?.data && Array.isArray(paymentsResponseData.data)) {
          paymentsData = paymentsResponseData.data;
        } else if (Array.isArray(paymentsResponseData)) {
          paymentsData = paymentsResponseData;
        } else if (Array.isArray(paymentsRes.data)) {
          paymentsData = paymentsRes.data;
        }

        const validPayments = paymentsData
          .filter((item) => item !== null && item !== undefined)
          .map((item) => ({
            ...item,
            id: `payment-${item.id}`,
            original_id: item.id,
            uuid: item.uuid,
            customer_id: item.customerId || item.customer_id || null,
            vendor_id: item.vendorId || item.vendor_id || null,
            amount: item.amount || 0,
            method: item.method || 'N/A',
            status: item.status || 'created',
            type: 'Payment',
            created_at: item.created_at,
          }));

        const combinedData = [...validCollections, ...validPayments];
        combinedData.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

        setHistoryData(combinedData);
      } catch (err) {
        console.error('Failed to fetch collections history:', err);
        setHistoryData([]);
      }
    },
    [drivers]
  );

  // Fetch history on mount with default filters (after drivers are loaded)
  useEffect(() => {
    if (drivers.length > 0 || historyFilterState.driver === 'All') {
      fetchHistory({
        driver: historyFilterState.driver,
        from: historyFilter.from,
        to: historyFilter.to,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drivers.length]); // Run when drivers are loaded or on mount

  // Ensure body can scroll when CashierPortal is mounted
  useEffect(() => {
    // Override AdminLayout.css which sets overflow: hidden
    const originalBodyOverflow = document.body.style.overflow;
    const originalBodyHeight = document.body.style.height;
    const originalHtmlOverflow = document.documentElement.style.overflow;
    const originalHtmlHeight = document.documentElement.style.height;
    const rootElement = document.getElementById('root');
    const originalRootOverflow = rootElement?.style.overflow;
    const originalRootHeight = rootElement?.style.height;

    // Force enable scrolling
    document.body.style.overflow = 'auto';
    document.body.style.height = 'auto';
    document.body.style.maxHeight = 'none';
    document.documentElement.style.overflow = 'auto';
    document.documentElement.style.height = 'auto';
    document.documentElement.style.maxHeight = 'none';

    if (rootElement) {
      rootElement.style.overflow = 'auto';
      rootElement.style.height = 'auto';
      rootElement.style.maxHeight = 'none';
    }

    return () => {
      // Reset on unmount
      document.body.style.overflow = originalBodyOverflow;
      document.body.style.height = originalBodyHeight;
      document.body.style.maxHeight = '';
      document.documentElement.style.overflow = originalHtmlOverflow;
      document.documentElement.style.height = originalHtmlHeight;
      document.documentElement.style.maxHeight = '';
      if (rootElement) {
        rootElement.style.overflow = originalRootOverflow || '';
        rootElement.style.height = originalRootHeight || '';
        rootElement.style.maxHeight = '';
      }
    };
  }, []);

  // ⭐ useEffect HOISTED TO TOP LEVEL OF THIS COMPONENT ⭐
  // ✅ Fetch drivers
  useEffect(() => {
    const fetchDrivers = async () => {
      try {
        const first = await authAxios.get('/drivers', { params: { page: 1 } });
        const firstPaginated = first.data?.data || {};
        const firstPage = Array.isArray(firstPaginated.data) ? firstPaginated.data : [];
        const lastPage = firstPaginated.last_page || 1;

        if (lastPage <= 1) {
          setDrivers(firstPage);
          return;
        }

        const pageNums = Array.from({ length: lastPage - 1 }, (_, i) => i + 2);
        const rest = await Promise.all(
          pageNums.map((p) => authAxios.get('/drivers', { params: { page: p } }))
        );
        const restDrivers = rest.flatMap((r) => r.data?.data?.data || []);
        setDrivers([...firstPage, ...restDrivers]);
      } catch (error) {
        console.error('Error fetching drivers:', error);
        setDrivers([]);
      }
    };
    fetchDrivers();
  }, []);

  // ✅ Helpers
  const getCustomerName = (customerId, customerObj) => {
    // First check if customer object is passed directly (from API response)
    if (customerObj && customerObj.name) {
      return customerObj.name;
    }
    // Then try to find by ID in customers array
    const customer = customers.find((c) => c.id === customerId);
    return customer ? customer.name : 'Unknown Customer';
  };

  const getVendorName = (vendorId) => {
    const vendor = vendors.find((v) => v.id === vendorId);
    return vendor ? vendor.name : 'Unknown Vendor';
  };

  // ✅ Get status badge color variant for collections
  const getCollectionStatusVariant = (status) => {
    switch (status?.toLowerCase()) {
      case 'created':
        return 'warning'; // Yellow/Orange for pending
      case 'assigned':
        return 'primary'; // Blue for assigned
      case 'received':
      case 'collected':
        return 'info'; // Light blue for received/collected
      case 'submitted':
        return 'success'; // Green for submitted
      case 'verified':
        return 'success'; // Green for verified
      case 'rejected':
      case 'cancelled':
        return 'danger'; // Red for rejected/cancelled
      default:
        return 'secondary'; // Gray for unknown
    }
  };

  // ✅ Refetch all data function
  const refetchAllData = async () => {
    try {
      // Fetch summary
      const summaryRes = await authAxios.get('/dashboard/cashier-summary');
      const summaryData = summaryRes.data?.data || summaryRes.data || {};
      setSummary(summaryData);

      // Fetch customers
      const customersRes = await authAxios.get('/customers');
      const customersData = customersRes.data?.data?.data || [];
      setCustomers(Array.isArray(customersData) ? customersData : []);

      // Fetch vendors
      const vendorsRes = await authAxios.get('/vendors');
      const vendorsData = vendorsRes.data?.data?.data || [];
      setVendors(Array.isArray(vendorsData) ? vendorsData : []);

      // Fetch all driver pages
      const driversFirst = await authAxios.get('/drivers', { params: { page: 1 } });
      const driversFirstPaginated = driversFirst.data?.data || {};
      const driversFirstPage = Array.isArray(driversFirstPaginated.data) ? driversFirstPaginated.data : [];
      const driversLastPage = driversFirstPaginated.last_page || 1;
      if (driversLastPage > 1) {
        const driversRest = await Promise.all(
          Array.from({ length: driversLastPage - 1 }, (_, i) => i + 2).map((p) =>
            authAxios.get('/drivers', { params: { page: p } })
          )
        );
        setDrivers([...driversFirstPage, ...driversRest.flatMap((r) => r.data?.data?.data || [])]);
      } else {
        setDrivers(driversFirstPage);
      }

      // Fetch pending collections with pagination
      let allPendingCollections = [];
      let page = 1;
      let hasMore = true;

      while (hasMore) {
        const pendingRes = await authAxios.get('/collections', {
          params: {
            status: 'created',
            page: page,
            per_page: 100,
            start_date: getFirstDayOfMonth(),
            end_date: getLastDayOfMonth(),
          },
        });

        const responseData = pendingRes.data?.data || pendingRes.data;
        let collectionsData = [];

        if (responseData?.data && Array.isArray(responseData.data)) {
          collectionsData = responseData.data;
          hasMore = page < (responseData.last_page || 1);
        } else if (Array.isArray(responseData)) {
          collectionsData = responseData;
          hasMore = false;
        } else {
          hasMore = false;
        }

        const validCollections = collectionsData
          .filter((item) => item !== null && item !== undefined)
          .map((item) => ({
            ...item,
            id: item.id || item.uuid || null,
            customer_id: item.customer_id || null,
            vendor_id: item.vendor_id || null,
            amount: item.amount || 0,
            method: item.method || 'N/A',
            status: item.status || 'created',
            uuid: item.uuid || item.id || null,
          }));

        allPendingCollections = [...allPendingCollections, ...validCollections];

        if (hasMore && validCollections.length > 0) {
          page++;
        } else {
          break;
        }
      }
      setActivePendingCollections(allPendingCollections);

      // Fetch active collections
      const activeRes = await authAxios.get('/collections', {
        params: {
          status: 'assigned',
          start_date: getFirstDayOfMonth(),
          end_date: getLastDayOfMonth(),
        },
      });
      const activeData = activeRes.data?.data || [];
      const validActiveCollections = (
        Array.isArray(activeData) ? activeData : []
      )
        .filter((item) => item !== null && item !== undefined)
        .map((item) => ({
          ...item,
          id: item.id || item.uuid || null,
          customer_id: item.customer_id || null,
          vendor_id: item.vendor_id || null,
          amount: item.amount || 0,
          method: item.method || 'N/A',
          status: item.status || 'assigned',
        }));
      setActiveCollections(validActiveCollections);

      // Refresh collected list (no enrichment/mutation)
      await fetchCollected();

      // Fetch history with current filters
      await fetchHistory({
        driver: historyFilterState.driver,
        from: historyFilter.from,
        to: historyFilter.to,
      });
    } catch (err) {
      console.error('Error refetching all data:', err);
    }
  };

  // ✅ Assign driver to a collection
  const handleAssignDriver = async () => {
    // 🧾 Debug logs
    console.log('Driver ID:', selectedDriver);
    console.log('Collection ID:', selectedCollectionId);

    if (!selectedDriver || !selectedCollectionId) {
      alert('Please select a driver before assigning.');
      return;
    }

    try {
      await authAxios.post(
        `/collections/${selectedCollectionId}/assign`,
        {
          assigned_to: selectedDriver,
        }
      );

      alert('Driver assigned successfully!');
      setAssignedDriver(selectedDriver);
      setAssignModal(false);

      // 🔄 Refetch all data after driver assignment
      await refetchAllData();
    } catch (err) {
      console.error('❌ Error assigning driver:', err);
      alert(
        err.response?.data?.message ||
        'Error assigning driver. Please try again.'
      );
    }
  };

  const handleApproveRequest = async () => {
    if (!selectedRequest) return;

    try {
      // Get payment_id from the fetched collection data (payments[0].id)
      // The collection data should already be fetched in handleOpenModal
      let paymentId = selectedRequest.payments?.[0]?.id;

      // If payment_id is not in selectedRequest, fetch collection again
      if (!paymentId) {
        const collectionId = selectedRequest.id || selectedRequest.uuid;
        const collectionRes = await authAxios.get(
          `/collections/${collectionId}`
        );

        const collectionData = collectionRes.data?.data || collectionRes.data;
        paymentId = collectionData.payments?.[0]?.id;
      }

      if (!paymentId) {
        alert('Payment ID not found in collection data.');
        return;
      }

      // Use the payment_id to approve the payment
      const res = await authAxios.patch(`/payments/${paymentId}/approve`);

      if (res.status === 200) {
        alert('Payment approved successfully!');

        // Refresh all data
        await refetchAllData();

        handleClose(); // Close the modal
      }
    } catch (err) {
      console.error('Error approving request:', err.response || err);
      alert(err.response?.data?.message || 'Failed to approve request.');
    }
  };

  // Add local filter state for driver and status
  const [historyFilterState, setHistoryFilterState] = useState({
    driver: 'All',
    status: 'All',
  });

  // Filtered history for driver and status filter
  const filteredHistoryData = useMemo(() => {
    let data = historyData;
    const { driver, status } = historyFilterState;

    if (driver && driver !== 'All') {
      data = data.filter((entry) => {
        // Driver assigned statuses where "Assigned To" should match
        const driverAssignedStatuses = ['assigned', 'collected', 'received', 'submitted', 'verified'];
        const entryStatus = entry.status?.toLowerCase();

        // Check top-level properties of the normalized entry
        const isAssignedToDriver =
          String(entry.assigned_to) === String(driver) ||
          String(entry.assigned_to_name) === String(driver) ||
          String(entry.driver_name) === String(driver);

        // If status is one of the driver-assigned statuses, it MUST match the driver filter
        if (driverAssignedStatuses.includes(entryStatus)) {
          return isAssignedToDriver;
        }

        // For other statuses, we still apply the driver filter if it was ever assigned
        return isAssignedToDriver;
      });
    }

    if (status && status !== 'All') {
      data = data.filter((entry) => {
        const entryStatus = String(entry.status).toLowerCase();
        const filterStatus = String(status).toLowerCase();

        // Handle consolidated "Assigned" status
        if (filterStatus === 'assigned') {
          return entryStatus === 'assigned' || entryStatus === 'assigned to driver';
        }

        // Handle "Pending" which might be "created" in the data
        if (filterStatus === 'pending') {
          return entryStatus === 'pending' || entryStatus === 'created';
        }

        return entryStatus === filterStatus;
      });
    }
    return data;
  }, [historyData, historyFilterState]);

  const collectedList = useMemo(() => {
    return Array.isArray(collectedCollections) ? collectedCollections : [];
  }, [collectedCollections]);

  const toggleCollectionExpand = (collectionId) => {
    setExpandedCollections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(collectionId)) {
        newSet.delete(collectionId);
      } else {
        newSet.add(collectionId);
      }
      return newSet;
    });
  };

  return (
    <>
      <div
        style={{
          background: '#f0f5ff',
          minHeight: '100vh',
          position: 'relative',
          overflow: 'hidden',
          zIndex: 0,
        }}
      >
        {/* Abstract Background Shapes */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 0,
            pointerEvents: 'none',
          }}
        >
          {/* Large abstract shapes */}
          <div
            style={{
              position: 'absolute',
              width: '300px',
              height: '300px',
              borderRadius: '50%',
              background: 'rgba(147, 197, 253, 0.4)',
              top: '-100px',
              left: '-50px',
              animation: 'float 20s infinite ease-in-out',
            }}
          ></div>
          <div
            style={{
              position: 'absolute',
              width: '250px',
              height: '250px',
              borderRadius: '50%',
              background: 'rgba(186, 230, 253, 0.5)',
              top: '20%',
              right: '-80px',
              animation: 'float 20s infinite ease-in-out',
              animationDelay: '3s',
            }}
          ></div>
          <div
            style={{
              position: 'absolute',
              width: '200px',
              height: '200px',
              borderRadius: '50%',
              background: 'rgba(224, 242, 254, 0.6)',
              bottom: '10%',
              left: '10%',
              animation: 'float 20s infinite ease-in-out',
              animationDelay: '6s',
            }}
          ></div>
          <div
            style={{
              position: 'absolute',
              width: '180px',
              height: '180px',
              borderRadius: '50%',
              background: 'rgba(147, 197, 253, 0.3)',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              animation: 'float 20s infinite ease-in-out',
              animationDelay: '9s',
            }}
          ></div>
          <div
            style={{
              position: 'absolute',
              width: '150px',
              height: '150px',
              borderRadius: '50%',
              background: 'rgba(186, 230, 253, 0.4)',
              bottom: '-50px',
              right: '20%',
              animation: 'float 20s infinite ease-in-out',
              animationDelay: '12s',
            }}
          ></div>
          <div
            style={{
              position: 'absolute',
              width: '120px',
              height: '120px',
              borderRadius: '50%',
              background: 'rgba(224, 242, 254, 0.5)',
              top: '70%',
              right: '10%',
              animation: 'float 20s infinite ease-in-out',
              animationDelay: '15s',
            }}
          ></div>

          {/* Medium dots */}
          <div
            style={{
              position: 'absolute',
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: 'rgba(30, 64, 175, 0.5)',
              top: '12%',
              left: '30%',
              animation: 'floatMediumDot 18s infinite ease-in-out',
            }}
          ></div>
          <div
            style={{
              position: 'absolute',
              width: '70px',
              height: '70px',
              borderRadius: '50%',
              background: 'rgba(37, 99, 235, 0.45)',
              top: '35%',
              left: '65%',
              animation: 'floatMediumDot 18s infinite ease-in-out',
              animationDelay: '2s',
            }}
          ></div>
          <div
            style={{
              position: 'absolute',
              width: '85px',
              height: '85px',
              borderRadius: '50%',
              background: 'rgba(59, 130, 246, 0.5)',
              top: '55%',
              left: '20%',
              animation: 'floatMediumDot 18s infinite ease-in-out',
              animationDelay: '4s',
            }}
          ></div>
          <div
            style={{
              position: 'absolute',
              width: '75px',
              height: '75px',
              borderRadius: '50%',
              background: 'rgba(96, 165, 250, 0.4)',
              top: '25%',
              left: '75%',
              animation: 'floatMediumDot 18s infinite ease-in-out',
              animationDelay: '1s',
            }}
          ></div>
          <div
            style={{
              position: 'absolute',
              width: '90px',
              height: '90px',
              borderRadius: '50%',
              background: 'rgba(30, 64, 175, 0.45)',
              top: '65%',
              left: '50%',
              animation: 'floatMediumDot 18s infinite ease-in-out',
              animationDelay: '3s',
            }}
          ></div>
          <div
            style={{
              position: 'absolute',
              width: '65px',
              height: '65px',
              borderRadius: '50%',
              background: 'rgba(37, 99, 235, 0.4)',
              top: '8%',
              left: '55%',
              animation: 'floatMediumDot 18s infinite ease-in-out',
              animationDelay: '5s',
            }}
          ></div>
          <div
            style={{
              position: 'absolute',
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: 'rgba(59, 130, 246, 0.45)',
              top: '45%',
              left: '10%',
              animation: 'floatMediumDot 18s infinite ease-in-out',
              animationDelay: '1.5s',
            }}
          ></div>
          <div
            style={{
              position: 'absolute',
              width: '70px',
              height: '70px',
              borderRadius: '50%',
              background: 'rgba(96, 165, 250, 0.5)',
              top: '72%',
              left: '70%',
              animation: 'floatMediumDot 18s infinite ease-in-out',
              animationDelay: '3.5s',
            }}
          ></div>

          {/* Small dots */}
          <div
            style={{
              position: 'absolute',
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: 'rgba(147, 197, 253, 0.4)',
              top: '15%',
              left: '20%',
              animation: 'floatDot 15s infinite ease-in-out',
            }}
          ></div>
          <div
            style={{
              position: 'absolute',
              width: '35px',
              height: '35px',
              borderRadius: '50%',
              background: 'rgba(147, 197, 253, 0.4)',
              top: '25%',
              left: '60%',
              animation: 'floatDot 15s infinite ease-in-out',
              animationDelay: '1s',
            }}
          ></div>
          <div
            style={{
              position: 'absolute',
              width: '30px',
              height: '30px',
              borderRadius: '50%',
              background: 'rgba(147, 197, 253, 0.4)',
              top: '35%',
              left: '15%',
              animation: 'floatDot 15s infinite ease-in-out',
              animationDelay: '2s',
            }}
          ></div>
          <div
            style={{
              position: 'absolute',
              width: '45px',
              height: '45px',
              borderRadius: '50%',
              background: 'rgba(147, 197, 253, 0.4)',
              top: '45%',
              left: '70%',
              animation: 'floatDot 15s infinite ease-in-out',
              animationDelay: '0.5s',
            }}
          ></div>
          <div
            style={{
              position: 'absolute',
              width: '35px',
              height: '35px',
              borderRadius: '50%',
              background: 'rgba(147, 197, 253, 0.4)',
              top: '55%',
              left: '30%',
              animation: 'floatDot 15s infinite ease-in-out',
              animationDelay: '1.5s',
            }}
          ></div>
          <div
            style={{
              position: 'absolute',
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: 'rgba(147, 197, 253, 0.4)',
              top: '65%',
              left: '80%',
              animation: 'floatDot 15s infinite ease-in-out',
              animationDelay: '2.5s',
            }}
          ></div>
          <div
            style={{
              position: 'absolute',
              width: '30px',
              height: '30px',
              borderRadius: '50%',
              background: 'rgba(147, 197, 253, 0.4)',
              top: '20%',
              left: '45%',
              animation: 'floatDot 15s infinite ease-in-out',
              animationDelay: '3s',
            }}
          ></div>
          <div
            style={{
              position: 'absolute',
              width: '35px',
              height: '35px',
              borderRadius: '50%',
              background: 'rgba(147, 197, 253, 0.4)',
              top: '30%',
              left: '10%',
              animation: 'floatDot 15s infinite ease-in-out',
              animationDelay: '0.8s',
            }}
          ></div>
          <div
            style={{
              position: 'absolute',
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: 'rgba(147, 197, 253, 0.4)',
              top: '40%',
              left: '55%',
              animation: 'floatDot 15s infinite ease-in-out',
              animationDelay: '1.8s',
            }}
          ></div>
          <div
            style={{
              position: 'absolute',
              width: '30px',
              height: '30px',
              borderRadius: '50%',
              background: 'rgba(147, 197, 253, 0.4)',
              top: '50%',
              left: '25%',
              animation: 'floatDot 15s infinite ease-in-out',
              animationDelay: '2.8s',
            }}
          ></div>
        </div>
        <div
          className='container justify-content-center align-items-center'
          style={{ padding: '20px 0', position: 'relative', zIndex: 1 }}
        >
          <div className='row'>
            <div className='col-md-8 col-lg-8' style={{ margin: '0px auto' }}>
              {/* Header Card */}
              <div
                className='card shadow'
                style={{
                  background: 'linear-gradient(90deg, #3b82f6, #60a5fa)',
                }}
              >
                <div className='card-body'>
                  <div className='d-flex justify-content-between align-items-center'>
                    <h4 className='card-title text-center text-white flex-grow-1'>
                      Cashier Portal
                    </h4>
                    <Button variant='outline-danger' onClick={handleLogout}>
                      <FaSignOutAlt className='me-1' /> Logout
                    </Button>
                  </div>
                  <div className='text-center mt-3'>
                    <div className='d-flex justify-content-center align-items-center'>
                      <span className='text-white'>
                        Welcome{' '}
                        {userInfo.name.replace(/^\w/, (c) => c.toUpperCase())},
                      </span>
                    </div>
                    <div className='d-flex justify-content-center align-items-center'>
                      <span className='text-white'>
                        Branch: {userInfo.branch}
                      </span>
                    </div>
                  </div>
                  <div className='text-center mt-3'>
                    <div className='container'>
                      <div className='row'>
                        <div className='col-md-4'>
                          <div className='box'>
                            <h5 className='text-white'>
                              {summary.requests_due || 0}
                            </h5>
                            <p className='text-white'>Requests Due</p>
                          </div>
                        </div>
                        <div className='col-md-4'>
                          <div className='box'>
                            <h5 className='text-white'>
                              AED {summary.amount_due || '0.00'}
                            </h5>
                            <p className='text-white'>Amount Due</p>
                          </div>
                        </div>
                        <div className='col-md-4'>
                          <div className='box'>
                            <h5 className='text-white'>
                              {summary.active_collections || 0}
                            </h5>
                            <p className='text-white'>Active Collections</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tabs Card */}
              <div className='card shadow' style={{ background: '#ffffff' }}>
                <div className='card-body'>
                  <div className='container'>
                    {/* Tabs */}
                    <div className='gap-3 mb-4'>
                      <div className='row'>
                        <div className='col-md-3'>
                          <Button
                            className={`for_btn ${activeForm === 'formA' ? 'active' : ''
                              }`}
                            variant='primary'
                            onClick={() => setActiveForm('formA')}
                          >
                            <i className='fa fa-clock'></i> Pending
                          </Button>
                        </div>
                        <div className='col-md-3'>
                          <Button
                            className={`for_btn ${activeForm === 'formB' ? 'active' : ''
                              }`}
                            variant='success'
                            onClick={() => setActiveForm('formB')}
                          >
                            <i className='fa fa-play'></i> Active Collections
                          </Button>
                        </div>
                        <div className='col-md-3'>
                          <Button
                            className={`for_btn ${activeForm === 'formC' ? 'active' : ''
                              }`}
                            variant='warning'
                            onClick={() => setActiveForm('formC')}
                          >
                            <i className='fa fa-check-circle'></i> Collected
                          </Button>
                        </div>
                        <div className='col-md-3'>
                          <Button
                            className={`for_btn ${activeForm === 'formd' ? 'active' : ''
                              }`}
                            variant='danger'
                            onClick={() => setActiveForm('formd')}
                          >
                            <i className='fa fa-history'></i> History
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* Pending Requests (formA) */}
                    {activeForm === 'formA' && (
                      <>
                        {activePendingCollections.length === 0 ? (
                          <div className='text-center py-5'>
                            <p
                              className='text-muted'
                              style={{ fontSize: '18px' }}
                            >
                              No data available
                            </p>
                          </div>
                        ) : (
                          activePendingCollections
                            .filter((req) => req && (req.id || req.uuid))
                            .map((req) => {
                              // Ensure we have valid data
                              const safeReq = {
                                id: req.id || req.uuid || 'unknown',
                                customer_id: req.customer_id ?? null,
                                amount: req.amount ?? 0,
                                method: req.method ?? 'N/A',
                                status: req.status ?? 'created',
                                ...req,
                              };

                              return (
                                <Card key={safeReq.id} className='mb-3'>
                                  <Card.Body>
                                    <div className='row'>
                                      <div className='col-md-8 text-left'>
                                        <Card.Text
                                          style={{ textAlign: 'left', fontSize: '0.9rem' }}
                                        >
                                          <h5 className='mb-2 me-2'
                                            style={{
                                              fontSize: '1.1rem',
                                              fontWeight: '600',
                                            }}>Request #{safeReq.id}</h5>
                                          <strong>Customer:</strong>{' '}
                                          {safeReq.customer_name || 'None'}
                                          <br />
                                          <strong>Amount:</strong>{' '} AED {safeReq.amount || '0'}
                                          <br />
                                          <strong>Method:</strong>{' '} {formatPaymentMethod(safeReq.method)}
                                          <br />
                                          <strong>Status:</strong>{' '}
                                          <Badge bg='danger'>
                                            {safeReq.status || 'Pending'}
                                          </Badge>
                                        </Card.Text>
                                      </div>
                                      <div className='col-md-4'>
                                        <Button
                                          variant='success'
                                          onClick={() => {
                                            setSelectedCollectionId(safeReq.id);
                                            setAssignModal(true);
                                          }}
                                        >
                                          Assign
                                        </Button>
                                        <Button
                                          variant='secondary'
                                          onClick={() =>
                                            handleOpenDetailsModal(safeReq)
                                          }
                                          style={{ marginLeft: '10px' }}
                                        >
                                          View
                                        </Button>
                                      {['created', 'assigned', 'assign_to_driver', 'assigned to driver'].includes(req.status?.toString().trim().toLowerCase()) && (
                                        <Button
                                          variant='danger'
                                          size='sm'
                                          style={{
                                            padding: '2px 10px',
                                            fontSize: '15px',
                                            height: '30px',
                                          }}
                                          onClick={() => handleDeleteItem(req.id, req.type)}
                                        >
                                          Delete
                                        </Button>
                                      )}                                      
				    </div>
                                    </div>
                                  </Card.Body>
                                </Card>
                              );
                            })
                        )}
                      </>
                    )}

                    {/* Active Collections (formB) */}
                    {activeForm === 'formB' && (
                      <>
                        {activeCollections.length === 0 ? (
                          <div className='text-center py-5'>
                            <p
                              className='text-muted'
                              style={{ fontSize: '18px' }}
                            >
                              No data available
                            </p>
                          </div>
                        ) : (
                          activeCollections.map((req) => (
                            <Card key={req.id} className='mb-3'>
                              <Card.Body>
                                <div className='row'>
                                  <div className='col-md-8 text-left'>
                                    <Card.Text style={{ textAlign: 'left', fontSize: '0.9rem' }}>
                                      <h5 className='mb-2 me-2'
                                        style={{
                                          fontSize: '1.1rem',
                                          fontWeight: '600',
                                        }}>Request #{req.id}</h5>
                                      <strong>Customer:</strong>{' '}
                                      {req.customer_name || 'Unknown Customer'}
                                      <br />
                                      <strong>Amount:</strong>{' '} AED {req.amount || '0'}
                                      <br />
                                      <strong>Assigned To:</strong>{' '}
                                      {req.driver_name ||
                                        req.assigned_to_name ||
                                        assignedDriver ||
                                        'N/A'}
                                      <br />
                                      <strong>Status:</strong>{' '}
                                      <Badge bg='warning'>
                                        {req.status || 'assigned '}
                                      </Badge>
                                    </Card.Text>
                                  </div>
                                  <div className='col-md-4'>
                                    <Button
                                      variant='success'
                                      onClick={() => {
                                        setSelectedCollectionId(req.id);
                                        setSelectedDriver(
                                          req.assigned_to || ''
                                        );
                                        setAssignModal(true);
                                      }}
                                    >
                                      Reassign
                                    </Button>
                                    <Button
                                      variant='secondary'
                                      onClick={() =>
                                        handleOpenDetailsModal(req)
                                      }
                                      style={{ marginLeft: '10px' }}
                                    >
                                      Attachment
                                    </Button>
                                  </div>
                                </div>
                              </Card.Body>
                            </Card>
                          ))
                        )}
                      </>
                    )}

                    {/* Collected Requests (formC) */}
                    {activeForm === 'formC' && (
                      <>
                        {loadingCollected && (
                          <div className='text-center py-5'>Loading…</div>
                        )}

                        {errorCollected && (
                          <div className='text-center text-danger py-5'>
                            {errorCollected}
                          </div>
                        )}

                        {!loadingCollected &&
                          !errorCollected &&
                          collectedList.length === 0 && (
                            <div className='text-center py-5'>
                              <p
                                className='text-muted'
                                style={{ fontSize: '18px' }}
                              >
                                No collected requests
                              </p>
                            </div>
                          )}

                        {!loadingCollected &&
                          !errorCollected &&
                          collectedList.map((req) => {
                            const collectionId = req.id ?? req.uuid;
                            const isExpanded = expandedCollections.has(collectionId);
                            const payments = req.payments || [];
                            const hasPayments = payments.length > 0;

                            return (
                              <Card key={collectionId} className='mb-3'>
                                <Card.Body>
                                  <div className='d-flex justify-content-between align-items-center'>
                                    <div
                                      style={{
                                        flex: 1,
                                        textAlign: 'left',
                                        fontSize: '0.9rem',
                                      }}
                                    >
                                      <h5
                                        className='mb-2 me-2'
                                        style={{
                                          fontSize: '1.1rem',
                                          fontWeight: '600',
                                        }}
                                      >
                                        Request #{collectionId}
                                      </h5>
                                      <strong>Collection Code:</strong>{' '}
                                      {req.collection_code ?? req.code ?? 'N/A'}
                                      <br />
                                      <strong>Customer:</strong>{' '}
                                      {req.customer_name ||
                                        (req.customer_id
                                          ? getCustomerName(req.customer_id)
                                          : 'Unknown')}
                                      <br />
                                      <strong>Amount:</strong> AED {req.amount ?? '0'}
                                      <br />
                                      <strong>Method:</strong> {formatPaymentMethod(req.method)}
                                      <br />
                                      <strong>Status:</strong> {req.status ?? 'collected'}
                                      <br />
                                      <strong>Assigned To:</strong>{' '}
                                      {req.driver_name || req.assigned_to_name || 'Unassigned'}
                                      <br />
                                      <strong>Cashier Approved:</strong>{' '}
                                      {(payments.some(p => p.cashier_approved === 1 || p.cashier_approved === '1' || p.cashier_approved === true) || req.cashier_approved === 1 || req.cashier_approved === '1' || req.cashier_approved === true) ? (
                                        <Badge bg='success' style={{ fontSize: '0.75rem' }}>Yes</Badge>
                                      ) : (
                                        <Badge bg='secondary' style={{ fontSize: '0.75rem' }}>No</Badge>
                                      )}
                                    </div>

                                    <div
                                      className='d-flex align-items-center gap-2'
                                      style={{ flexDirection: 'column' }}
                                    >
                                      <Badge bg='info'>Collected</Badge>

                                      {(payments.some(p => p.cashier_approved === 1 || p.cashier_approved === '1' || p.cashier_approved === true) || req.cashier_approved === 1 || req.cashier_approved === '1' || req.cashier_approved === true) ? (
                                        <Badge
                                          bg='info'
                                          style={{ minWidth: '120px', padding: '8px' }}
                                        >
                                          Approved by Cashier
                                        </Badge>
                                      ) : (
                                        <Button
                                          variant='success'
                                          size='sm'
                                          onClick={() => handleOpenModal(req, 'approve')}
                                          style={{ minWidth: '120px' }}
                                        >
                                          Approve
                                        </Button>
                                      )}

                                      <Button
                                        variant='secondary'
                                        size='sm'
                                        onClick={() => handleOpenDetailsModal(req)}
                                        style={{ minWidth: '120px' }}
                                      >
                                        View Details
                                      </Button>
                                    </div>
                                  </div>

                                  {/* Expandable Payments Section */}
                                  {hasPayments && (
                                    <>
                                      <hr className='my-2' />
                                      <div>
                                        <Button
                                          variant='link'
                                          size='sm'
                                          onClick={() => toggleCollectionExpand(collectionId)}
                                          style={{ textDecoration: 'none', padding: 0, fontSize: '0.85rem' }}
                                        >
                                          <i className={`fa fa-chevron-${isExpanded ? 'up' : 'down'} me-1`}></i>
                                          {isExpanded ? 'Hide' : 'Show'} Payments ({payments.length})
                                        </Button>

                                        {isExpanded && (
                                          <div className='mt-2 ps-3'>
                                            {payments.map((payment, idx) => (
                                              <div key={payment.id || idx} className='mb-2 p-2 border rounded' style={{ backgroundColor: '#f8f9fa', fontSize: '0.85rem' }}>
                                                <div><strong>Payment ID:</strong> {payment.id}</div>
                                                <div><strong>Amount:</strong> AED {payment.amount ?? '0'}</div>
                                                <div><strong>Method:</strong> {formatPaymentMethod(payment.method)}</div>
                                                <div><strong>Status:</strong> {payment.status ?? 'N/A'}</div>
                                                <div>
                                                  <strong>Cashier Approved:</strong>{' '}
                                                  {(payment.cashier_approved === 1 || payment.cashier_approved === '1' || payment.cashier_approved === true) ? (
                                                    <Badge bg='success' style={{ fontSize: '0.7rem' }}>Yes</Badge>
                                                  ) : (
                                                    <Badge bg='secondary' style={{ fontSize: '0.7rem' }}>No</Badge>
                                                  )}
                                                </div>
                                                {payment.cashier_approved_at && (
                                                  <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>
                                                    Approved at: {new Date(payment.cashier_approved_at).toLocaleString()}
                                                  </div>
                                                )}
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    </>
                                  )}
                                </Card.Body>
                              </Card>
                            );
                          })}

                        <div className='mt-3 d-flex justify-content-end'>
                          <Button
                            variant='outline-primary'
                            onClick={fetchCollected}
                          >
                            Refresh
                          </Button>
                        </div>
                      </>
                    )}

                    {/* History (formd) */}
                    {activeForm === 'formd' && (
                      <Card className='p-4'>
                        <Card.Body>
                          <div className='mb-3'>
                            <h5 style={{ fontWeight: 600 }}>History</h5>

                            <div className='mb-3'>
                              {/* Row 1: Driver and Status */}
                              <div className='d-flex align-items-end gap-3 mb-3 flex-wrap'>
                                <div>
                                  <label className='me-2 d-block'>
                                    <b>Filter by Driver:</b>
                                  </label>
                                  <select
                                    className='form-select d-inline-block w-auto'
                                    value={historyFilterState.driver}
                                    onChange={(e) => setHistoryFilterState((prev) => ({ ...prev, driver: e.target.value }))}
                                  >
                                    <option value='All'>All Drivers</option>
                                    {drivers.map((d) => (
                                      <option key={d.id} value={d.name}>
                                        {d.name}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                <div>
                                  <label className='me-2 d-block'>
                                    <b>Filter by Status:</b>
                                  </label>
                                  <select
                                    className='form-select d-inline-block w-auto'
                                    value={historyFilterState.status}
                                    onChange={(e) => setHistoryFilterState((prev) => ({ ...prev, status: e.target.value }))}
                                  >
                                    <option value='All'>All Statuses</option>
                                    <option value='assigned'>Assigned</option>
                                    <option value='collected'>Collected</option>
                                    <option value='received'>Received</option>
                                    <option value='submitted'>Submitted</option>
                                    <option value='verified'>Verified</option>
                                    <option value='pending'>Pending</option>
                                    <option value='rejected'>Rejected</option>
                                    <option value='cancelled'>Cancelled</option>
                                  </select>
                                </div>
                              </div>

                              {/* Row 2: Date Pickers */}
                              <div className='d-flex align-items-end gap-3 mb-3 flex-wrap'>
                                <div>
                                  <div className='mb-1'><b>From:</b></div>
                                  <Form.Control
                                    type='date'
                                    name='from'
                                    value={historyFilter.from}
                                    onChange={(e) =>
                                      setHistoryFilter((prev) => ({
                                        ...prev,
                                        from: e.target.value,
                                      }))
                                    }
                                    style={{ width: '200px' }}
                                  />
                                </div>
                                <div>
                                  <div className='mb-1'><b>To:</b></div>
                                  <Form.Control
                                    type='date'
                                    name='to'
                                    value={historyFilter.to}
                                    onChange={(e) =>
                                      setHistoryFilter((prev) => ({
                                        ...prev,
                                        to: e.target.value,
                                      }))
                                    }
                                    style={{ width: '200px' }}
                                  />
                                </div>
                              </div>

                              {/* Row 3: Buttons */}
                              <div className='d-flex gap-2'>
                                <Button
                                  variant='primary'
                                  size='sm'
                                  onClick={() => {
                                    fetchHistory({
                                      driver: historyFilterState.driver,
                                      from: historyFilter.from,
                                      to: historyFilter.to,
                                    });
                                  }}
                                  style={{ height: '38px', minWidth: '100px' }}
                                >
                                  <FaSearch className='me-1' />
                                  Filter
                                </Button>
                                <Button
                                  variant='outline-secondary'
                                  size='sm'
                                  onClick={() => {
                                    setHistoryFilterState({ driver: 'All', status: 'All' });
                                    const defaultFrom = getFirstDayOfMonth();
                                    const defaultTo = getLastDayOfMonth();
                                    setHistoryFilter({
                                      from: defaultFrom,
                                      to: defaultTo,
                                    });
                                    fetchHistory({
                                      driver: 'All',
                                      from: defaultFrom,
                                      to: defaultTo,
                                    });
                                  }}
                                  style={{ height: '38px', minWidth: '100px' }}
                                >
                                  Clear
                                </Button>
                              </div>
                            </div>
                          </div>

                          {filteredHistoryData.length === 0 ? (
                            <div className='text-center py-5'>
                              <p
                                className='text-muted'
                                style={{ fontSize: '18px' }}
                              >
                                No data available
                              </p>
                            </div>
                          ) : (
                            filteredHistoryData.map((req) => (
                              <Card key={req.id || req.uuid} className='mb-3'>
                                <Card.Body>
                                  <div className='row'>
                                    <div className='col-md-8 text-left'>
                                      <Card.Text style={{ textAlign: 'left', fontSize: '0.9rem' }}>
                                        <h5 className='mb-2 me-2 d-flex align-items-center'
                                          style={{
                                            fontSize: '1.1rem',
                                            fontWeight: '600',
                                          }}>
                                          Request #{req.uuid || req.original_id || req.id}
                                          <Badge bg="secondary" className="ms-2" style={{ fontSize: '0.7rem' }}>{req.type}</Badge>
                                        </h5>
                                        {req.customer_id ? (
                                          <>
                                            <b>Customer:</b>{' '}
                                            {req.customer_name || getCustomerName(req.customer_id)}{' '}
                                            <br />
                                          </>
                                        ) : req.vendor_id ? (
                                          <>
                                            <b>Vendor:</b>{' '}
                                            {req.vendor_name || getVendorName(req.vendor_id)}{' '}
                                            <br />
                                          </>
                                        ) : req.customer_name ? (
                                          <>
                                            <b>Customer:</b>{' '}
                                            {req.customer_name}{' '}
                                            <br />
                                          </>
                                        ) : req.vendor_name ? (
                                          <>
                                            <b>Vendor:</b>{' '}
                                            {req.vendor_name}{' '}
                                            <br />
                                          </>
                                        ) : (
                                          <>
                                            <b>Entity:</b> Unknown <br />
                                          </>
                                        )}
                                        <b>Amount:</b> AED {req.amount || 0}{' '}
                                        <br />
                                        <b>Method:</b> {formatPaymentMethod(req.method)}{' '}
                                        <br />
                                        <b>Status:</b>{' '}
                                        <Badge
                                          bg={getCollectionStatusVariant(
                                            req.status
                                          )}
                                        >
                                          {req.status || 'N/A'}
                                        </Badge>
                                        <br />
                                        <b>Assigned To:</b>{' '}
                                        {req.driver_name ||
                                          req.assigned_to_name ||
                                          'Unassigned'}
                                        <br />
                                        {req.created_at && (
                                          <>
                                            <b>Created At:</b>{' '}
                                            {new Date(
                                              req.created_at
                                            ).toLocaleString()}
                                          </>
                                        )}
                                      </Card.Text>
                                    </div>
                                    <div className='col-md-4 d-flex align-items-center justify-content-end gap-2'>
                                      <Button
                                        variant='primary'
                                        size='sm'
                                        style={{
                                          padding: '2px 10px',
                                          fontSize: '15px',
                                          height: '30px',
                                        }}
                                        onClick={() =>
                                          handleOpenDetailsModal(req)
                                        } // ✅ USE NEW HANDLER for history item details
                                      >
                                        View
                                      </Button>
                                    </div>
                                  </div>
                                </Card.Body>
                              </Card>
                            ))
                          )}
                        </Card.Body>
                      </Card>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div >

      {/* Assign/Reassign Modal */}
      < Modal show={assignModal} onHide={() => setAssignModal(false)} centered >
        <Modal.Header closeButton>
          <Modal.Title>Assign Driver</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <select
            className='form-control'
            value={selectedDriver}
            onChange={(e) => setSelectedDriver(e.target.value)}
          >
            <option value=''>-- Select Driver --</option>
            {drivers.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setAssignModal(false)}>
            Cancel
          </Button>
          <Button
            variant='primary'
            onClick={handleAssignDriver}
            disabled={!selectedDriver}
          >
            Assign
          </Button>
        </Modal.Footer>
      </Modal >

      {/* Approval Modal */}
      < Modal show={show} onHide={handleClose} centered >
        <Modal.Header closeButton>
          <Modal.Title>
            {modalType === 'approve' ? 'Approve Request' : 'Request Details'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRequest && modalType === 'approve' && (
            <>
              <p>
                Are you sure you want to approve request{' '}
                <strong>#{selectedRequest.uuid || selectedRequest.id}</strong>?
              </p>
              <p className='mb-1'>
                <strong>Driver:</strong>{' '}
                {selectedRequest.driver?.name ||
                  selectedRequest.driver_name ||
                  'N/A'}
              </p>
              <p className='mb-1'>
                <strong>Method:</strong> {selectedRequest.method || 'N/A'}
              </p>
              <p className='mb-1'>
                <strong>Amount:</strong> AED {selectedRequest.amount || '0'}
              </p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={handleClose}>
            Cancel
          </Button>
          {modalType === 'approve' &&
            selectedRequest?.status?.toLowerCase() === 'collected' && (
              <Button variant='success' onClick={handleApproveRequest}>
                Confirm Approve
              </Button>
            )}
        </Modal.Footer>
      </Modal >

      {/* ✅ Collection Details Modal */}
      < CollectionDetailsModal
        show={showDetailsModal}
        handleClose={handleCloseDetailsModal}
        collection={viewedRequest}
        getCustomerName={getCustomerName}
        getVendorName={getVendorName}
      />
    </>
  );
};

export default CashierPortal;